import { enqueueSnackbar } from 'notistack';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    DicomBase,
    GenerationModelDetails,
    ObjectStatus,
} from '~/domain/dicom/dicomBase';
import {
    dicomProcessorState,
    filesProcessedState,
    filesToProcessState,
} from '~/state/atoms/dicomProcessorAtoms';
import { patientRepositoryState } from '~/state/atoms/patientAtoms';
import { exportableItemsCountState } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { processFile } from '~/utils/io/files/FileHandler';

type ProcessDicomFilesParams = {
    onFileProcessed: (file: DicomBase) => void;
};

export default function useProcessDicomFiles(
    params: ProcessDicomFilesParams = { onFileProcessed: () => {} }
) {
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const patientRepository = useRecoilValue(patientRepositoryState);
    const setFilesToProcess = useSetRecoilState(filesToProcessState);
    const setFilesProcessed = useSetRecoilState(filesProcessedState);
    const setExportableItemsCount = useSetRecoilState(
        exportableItemsCountState
    );

    function resetProcessingState() {
        setFilesProcessed(0);
        setFilesToProcess(undefined);
    }

    function handleFilesProcessed() {
        try {
            return dicomProcessor.toRepository(patientRepository);
        } catch (error: Error | unknown) {
            if (error instanceof Error) {
                handleError(error, `Failed to process files: ${error.message}`);
            } else {
                handleError(error, 'Failed to process files');
            }
        } finally {
            resetProcessingState();
        }
    }

    function checkForExistingRTStructs(sliceData: DicomBase): boolean {
        const existingPatient = patientRepository.getByPatientId(
            sliceData.datasetWrapper.PatientID
        );
        if (existingPatient) {
            const existingStructureSet = existingPatient.structureSets.find(
                (ss) =>
                    ss.dicomStructureSet.sopInstanceUID ===
                    sliceData.sopInstanceUID
            );
            if (existingStructureSet) {
                return true;
            } else {
                return false;
            }
        }
        return false;
    }

    function checkForExistingRTDoses(sliceData: DicomBase): boolean {
        const existingPatient = patientRepository.getByPatientId(
            sliceData.datasetWrapper.PatientID
        );
        if (existingPatient) {
            const existingDose = existingPatient.doses.find(
                (dose) => dose.dicomUid === sliceData.sopInstanceUID
            );
            if (existingDose) {
                return true;
            } else {
                return false;
            }
        }
        return false;
    }

    async function processFiles(
        files: File[],
        objectStatus: ObjectStatus,
        model?: GenerationModelDetails
    ) {
        setFilesToProcess(files.length);
        try {
            for await (const file of files) {
                const sliceData = await processFile(
                    file,
                    dicomProcessor,
                    objectStatus,
                    model
                );
                if (sliceData) {
                    if (checkForExistingRTStructs(sliceData)) {
                        enqueueSnackbar(
                            'This RTStruct has already been uploaded.',
                            { variant: 'warning' }
                        );
                        continue;
                    }
                    if (checkForExistingRTDoses(sliceData)) {
                        enqueueSnackbar(
                            'This RTDose has already been uploaded.',
                            { variant: 'warning' }
                        );
                        continue;
                    }
                    params.onFileProcessed(sliceData);
                }
                setFilesProcessed((prevFilesUploaded) => prevFilesUploaded + 1);
            }
            return handleFilesProcessed();
        } catch (error) {
            handleError(error, 'Error processing files');
            resetProcessingState();
        }
    }

    const processImportedFiles = async (files: File[]) => {
        return processFiles(files, ObjectStatus.IMPORTED);
    };

    const processCreatedFiles = async (
        files: File[],
        model?: GenerationModelDetails
    ) => {
        const processed = await processFiles(files, ObjectStatus.NEW, model);
        setExportableItemsCount((prevCount) => prevCount + 1);
        return processed;
    };

    return { processImportedFiles, processCreatedFiles };
}
